import type { ReactNode } from 'react'
import { Button, Form, Input, Space, Tooltip } from 'antd'
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import type { NamePath } from 'antd/es/form/interface'
import { maxChars } from './form'

type RowControlsProps = {
  index: number
  count: number
  label: string
  move: (from: number, to: number) => void
  remove: (index: number) => void
}

/** Up, down and remove for one row of a list; the spec lets every list in the profile be reordered. */
export function RowControls({ index, count, label, move, remove }: RowControlsProps) {
  // An explicit false would override a disabled Form, so leave the prop unset unless it's true.
  return (
    <Space.Compact>
      <Tooltip title="Move up">
        <Button
          icon={<ArrowUpOutlined />}
          aria-label={`Move ${label} ${index + 1} up`}
          disabled={index === 0 || undefined}
          onClick={() => move(index, index - 1)}
        />
      </Tooltip>
      <Tooltip title="Move down">
        <Button
          icon={<ArrowDownOutlined />}
          aria-label={`Move ${label} ${index + 1} down`}
          disabled={index === count - 1 || undefined}
          onClick={() => move(index, index + 1)}
        />
      </Tooltip>
      <Tooltip title="Remove">
        <Button icon={<DeleteOutlined />} aria-label={`Remove ${label} ${index + 1}`} onClick={() => remove(index)} />
      </Tooltip>
    </Space.Compact>
  )
}

type LineListProps = {
  name: NamePath
  /** Lower case, for labels such as "Remove note 2". */
  label: string
  max: number
  maxLength: number
  addLabel: string
  placeholder?: string
  emptyMessage: string
}

/** A list of lines of text, as `workflowNotes`, `notes` and an objective's initiatives are stored. */
export function LineList({ name, label, max, maxLength, addLabel, placeholder, emptyMessage }: LineListProps) {
  return (
    <Form.List name={name}>
      {(fields, { add, remove, move }) => (
        <>
          {fields.map((field, index) => (
            <div key={field.key} className="flex items-start gap-2">
              <Form.Item
                name={field.name}
                className="grow"
                rules={[{ required: true, whitespace: true, message: emptyMessage }, maxChars(maxLength)]}
              >
                <Input.TextArea
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  maxLength={maxLength}
                  placeholder={index === 0 ? placeholder : undefined}
                  aria-label={`${label} ${index + 1}`}
                />
              </Form.Item>
              <RowControls index={index} count={fields.length} label={label} move={move} remove={remove} />
            </div>
          ))}
          {fields.length < max && (
            <Button icon={<PlusOutlined />} onClick={() => add('')}>
              {addLabel}
            </Button>
          )}
        </>
      )}
    </Form.List>
  )
}

export function Part({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-brand-100 bg-white p-5">
      <h3 className="font-display text-base font-semibold text-gray-900 mb-4">{title}</h3>
      {children}
    </section>
  )
}
